import type { DisplayCue } from "@expert/contentful";
import type { Logger } from "@expert/logging";
import type { KyInstance } from "ky";

import { HTTPError } from "ky";
import pDebounce from "p-debounce";

import type { SalesAnalytics } from "../sales-context/SalesContext.types";

import { useDetectedCuesStore } from "../stores/useDetectedCuesStore";
import { useProductOfferStore } from "../stores/useProductOfferStore";
import { EligibilityStatus, type EligibilityMeta, type EligibilityRequest, type EligibleProduct } from "../types";
import { applyCohortContentOverrides } from "./cohortContentOverrides";
import { type EligibilityErrorResponse, INVALID_MDN_CODE, InvalidMdnError } from "./eligibility.errors";
import {
    buildEligibilityResultLog,
    logSelectedOfferReplacement,
    logSupersededEligibilityResult,
} from "./eligibility.helpers";

export type { EligibilityRequest, EligibleProduct, EligibilityMeta };

const ELIGIBILITY_DEBOUNCE_TIME = 500;
const lastCheckTimeBySession = new Map<string, number>();

/**
 * The `v2/eligibility` response shape (what the wire fixtures and mocks are typed against). Not what
 * `getEligibility` resolves with: that is an `EligibilityReceipt`, and the products live in the store.
 */
export type CheckEligibilityResult = {
    productConfigs: EligibleProduct[];
    meta: EligibilityMeta;
    displayCues?: DisplayCue[];
    /**
     * Client-side, not from the API: when this result's request was sent (`Date.now()`), so a slow
     * response can be told apart from a newer one.
     */
    requestedAt?: number;
};

/** What `getEligibility` resolves with. The products live in the store; read them there. */
export type EligibilityReceipt = { requestedAt: number };

type SentCheck = { requestedAt: number; done: Promise<void>; resolve: () => void; reject: (error: unknown) => void };

// The newest check sent per session. `done` settles once that check has written the store (or failed).
const latestSentBySession = new Map<string, SentCheck>();

const createSentCheck = (requestedAt: number): SentCheck => {
    let resolve!: () => void;
    let reject!: (error: unknown) => void;
    const done = new Promise<void>((res, rej) => {
        resolve = res;
        reject = rej;
    });
    // A waiter may never attach; don't let a failed check surface as an unhandled rejection.
    done.catch(() => {});
    return { requestedAt, done, resolve, reject };
};

export const getEligibility = async (
    client: KyInstance,
    logger: Logger,
    analytics: SalesAnalytics,
    eligibility: EligibilityRequest,
    isCohortFlagOn = false,
): Promise<EligibilityReceipt> => {
    const { sessionId } = eligibility;
    const now = performance.now();
    const lastCheckTime = lastCheckTimeBySession.get(sessionId);
    const timeSinceLastCheck = lastCheckTime === undefined ? undefined : now - lastCheckTime;
    logger.info(
        { sessionId, timeSinceLastCheck, isPrecheck: eligibility.eligibilityAttributes?.isPrecheck },
        "Eligibility check triggered",
    );

    if (timeSinceLastCheck !== undefined && timeSinceLastCheck < ELIGIBILITY_DEBOUNCE_TIME) {
        logger.warn(
            {
                sessionId,
                timeSinceLastCheck,
                isPrecheck: eligibility.eligibilityAttributes?.isPrecheck,
            },
            `Eligibility check triggered more than once within ${ELIGIBILITY_DEBOUNCE_TIME}ms. Debouncing...`,
        );
    }
    lastCheckTimeBySession.set(sessionId, now);

    const { requestedAt } = await getSessionDebouncer(sessionId)(
        client,
        logger,
        analytics,
        eligibility,
        isCohortFlagOn,
    );
    try {
        await waitForNewerChecks(sessionId, requestedAt);
    } catch (error: unknown) {
        // This check's own request succeeded; the error is the newer check's, which logged its API failure.
        // Log here too, so a rejection from a call that never failed at the API can be traced to its cause.
        logger.warn(
            { error, sessionId, requestedAt },
            "Superseded eligibility check failed: the newer check that superseded it also failed",
        );
        throw error;
    }
    return { requestedAt };
};

// One debouncer per session. A shared one let a second session's call within the window replace the
// first's, so the first session's request was never sent and its caller resolved with the other's check.
const debouncerBySession = new Map<string, typeof performEligibilityCheck>();

const getSessionDebouncer = (sessionId: string) => {
    let debouncer = debouncerBySession.get(sessionId);
    if (!debouncer) {
        debouncer = pDebounce(performEligibilityCheck, ELIGIBILITY_DEBOUNCE_TIME);
        debouncerBySession.set(sessionId, debouncer);
    }
    return debouncer;
};

// A check sent after this one has the live quotes. Wait for the newest one to settle, so the store holds
// them when this resolves. Repeats while even newer checks are sent. Rejects only if the newest check
// failed: one that fails while an even newer check is in flight is not the last word, so keep waiting.
const waitForNewerChecks = async (sessionId: string, requestedAt: number): Promise<void> => {
    let latest = latestSentBySession.get(sessionId);
    while (latest && latest.requestedAt > requestedAt) {
        try {
            await latest.done;
        } catch (error: unknown) {
            const current = latestSentBySession.get(sessionId);
            if (current === latest) throw error;
            latest = current;
            continue;
        }
        if (latestSentBySession.get(sessionId) === latest) return;
        latest = latestSentBySession.get(sessionId);
    }
};

const performEligibilityCheck = async (
    client: KyInstance,
    baseLogger: Logger,
    analytics: SalesAnalytics,
    eligibility: EligibilityRequest,
    isCohortFlagOn: boolean,
): Promise<{ requestedAt: number }> => {
    const logger = baseLogger.child({ module: "eligibility" });

    // Stamped at send, not on receipt: USP invalidates quotes in the order it handles the
    // requests, so a slow response that lands after a newer one still carries dead quotes.
    const requestedAt = Date.now();
    const sent = createSentCheck(requestedAt);
    latestSentBySession.set(eligibility.sessionId, sent);
    try {
        const salesApiRes = await client
            .post("v2/eligibility", {
                json: eligibility,
                retry: 0,
            })
            .json<CheckEligibilityResult>();

        handleResponse(logger, analytics, eligibility, { ...salesApiRes, requestedAt }, isCohortFlagOn);
        sent.resolve();
        return { requestedAt };
    } catch (error: unknown) {
        // Older checks waiting on this one reject with the same classified error as its own caller.
        const failure = classifyEligibilityError(logger, error);
        sent.reject(failure);
        throw failure;
    }
};

const classifyEligibilityError = (logger: Logger, error: unknown): unknown => {
    // The eligibility API returns a 400 with `{ code: "INVALID_MDN", message }`
    // when the customer's phone number is invalid. Classify it into a typed error
    // here; the UI uses it to offer inline MDN correction. Read `error.data`, not
    // `error.response.json()`: ky 2 consumes the error body before throwing (ew#7426).
    if (error instanceof HTTPError && error.response.status === 400) {
        const body =
            typeof error.data === "object" && error.data !== null
                ? (error.data as EligibilityErrorResponse)
                : undefined;
        if (!body) {
            logger.warn({ error }, "eligibility 400 body was not a JSON object");
        }
        if (body?.code === INVALID_MDN_CODE) {
            logger.warn({ code: body.code }, "eligibility rejected: invalid MDN");
            return new InvalidMdnError(body.message);
        }
    }
    logger.warn({ error }, "eligibility API call failed");
    return error;
};

const handleResponse = (
    logger: Logger,
    analytics: SalesAnalytics,
    eligibility: EligibilityRequest,
    checkEligibilityResult: CheckEligibilityResult,
    isCohortFlagOn: boolean,
): void => {
    const isPrecheck = !!eligibility.eligibilityAttributes?.isPrecheck;

    // RDESQ-1988: cohort-only checklist verbiage swap. No-op when flag is off.
    checkEligibilityResult.productConfigs = applyCohortContentOverrides(
        checkEligibilityResult.productConfigs,
        isCohortFlagOn,
    );

    logger.info(buildEligibilityResultLog(eligibility, checkEligibilityResult.productConfigs), "Eligibility result");

    // `isPrecheck` rides on every event below. These fire on prechecks as well as real,
    // expert-initiated checks — deliberately, so precheck volume stays observable — but until now
    // carried no marker saying which they were, so nothing downstream could tell them apart. A
    // precheck runs against the incoming caller MDN before the expert has confirmed anything, so
    // without this flag `CustomerValidated` counts a validation that has not happened yet, against
    // an account that may not be the one transacted on.
    checkEligibilityResult.productConfigs.forEach((result) => {
        let dispatcher = analytics.dispatcher.withExtra({
            productSku: result.productSku,
            productOfferId: result.id,
            eligibilityStatus: result.status,
            isPrecheck,
        });

        if (eligibility.reservationSid) {
            dispatcher = dispatcher.withIdentity("ReservationSid", eligibility.reservationSid);
        }

        void dispatcher.dispatchBusinessEvent("CustomerValidated");
        void dispatcher.dispatchAction("ApiResult", "EligibilityStatus");
        analytics.trackFullStoryEvent("EligibilityStatus", {
            productSku: result.productSku,
            eligibilityStatus: result.status,
            productOfferId: result.id,
            reservationSid: eligibility.reservationSid ?? "unknown",
            sessionId: eligibility.sessionId,
            partner: eligibility.partner,
            hasAddress: !!eligibility.address,
            isPrecheck,
        });
    });

    // Skip store mutations on precheck. The precheck runs against the incoming
    // caller MDN before the expert has confirmed; populating the offer store
    // here causes CustomerOffers to skip its post-verification refetch (gating
    // logic treats the precheck-seeded products as already-fetched), and any
    // cues seeded from the precheck would reflect the incoming MDN, not what
    // the expert actually entered.
    const isSuperseded =
        !isPrecheck &&
        useProductOfferStore
            .getState()
            .isEligibilityResultSuperseded(eligibility.sessionId, checkEligibilityResult.requestedAt);
    if (isSuperseded) {
        logSupersededEligibilityResult(logger, eligibility.sessionId, checkEligibilityResult.requestedAt);
    }

    if (!isPrecheck && !isSuperseded) {
        const replacement = useProductOfferStore
            .getState()
            .updateProducts(
                eligibility.sessionId,
                checkEligibilityResult.productConfigs,
                checkEligibilityResult.displayCues,
                checkEligibilityResult.requestedAt,
            );
        logSelectedOfferReplacement(logger, eligibility.sessionId, replacement);
        if (checkEligibilityResult.meta) {
            useProductOfferStore
                .getState()
                .updateEligibilityMetaBySessionId(eligibility.sessionId, checkEligibilityResult.meta);
        }

        const hasExistingCues = !!useDetectedCuesStore.getState().getDisplayCues(eligibility.sessionId).length;
        if (!hasExistingCues && checkEligibilityResult.displayCues) {
            useDetectedCuesStore
                .getState()
                .setDefaultPartnerCues(eligibility.sessionId, checkEligibilityResult.displayCues);
        }
    }

    analytics.sendToHoneycomb(eligibility.sessionId, {
        eligibility: checkEligibilityResult.productConfigs
            .filter((p) => p.status === EligibilityStatus.Eligible)
            .map((p) => ({
                productSku: p.productSku,
                productOfferId: p.id,
                eligibilityStatus: p.status,
                displayName: useProductOfferStore.getState().getProductInfo(eligibility.sessionId, p.productSku)
                    ?.displayName,
            })),
    });
};
